import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  controleEstoqueKeys,
  fetchEstoqueFresco,
  fetchListaProdutosControleEstoque,
  inserirMovimentacao,
} from '@/services/controleEstoqueService';
import type {
  IControleEstoqueFormData,
  IEstoqueFrescoItem,
  IFiltroTabela,
  IInserirMovimentacaoPayload,
  IProdutoControleEstoque,
  TColunaOrdenacao,
} from '@/types/controleEstoque';
import { useAnalytics, useErrors, useMetrics } from '@/observability';
import './controle-estoque.css';
import { Card } from '@/components/Card/Card';
import { Table } from '@/components/Table/Table';
import { Modal } from '@/components/Modal/Modal';

export const Route = createFileRoute('/_authenticated/estoque/controle-estoque/')({
  component: ControleEstoquePage,
});

const INITIAL_FORM_DATA: IControleEstoqueFormData = {
  tipoMovimentacao: 'entrada',
  codigoProduto: '',
  descricaoProduto: '',
  lote: '',
  destino: '',
  quantidade: '',
  validade: '',
  finalidade: '',
};

const INITIAL_FILTRO_TABELA: IFiltroTabela = {
  buscaGeral: '',
  colunaFiltro: 'todos',
  ordenacao: { coluna: 'codigo_produto', direcao: 'asc' },
};

function normalizarData(data: string | undefined): string {
  if (!data) return '';
  try {
    return new Date(data).toISOString().split('T')[0];
  } catch {
    return data;
  }
}

const columns: string[] = ['Código', 'Descrição', 'Lote', 'Validade', 'Quantidade', 'Finalidade'];

const FINALIDADE_LABELS: Record<string, string> = {
  VENDA: 'Venda',
  PRODUCAO: 'Produção',
  PRODUÇÃO: 'Produção',
  REVENDA: 'Revenda',
  INDUSTRIALIZAÇÃO: 'Industrialização',
  EXPORTAÇÃO: 'Exportação',
};

function formatarFinalidade(finalidade: string | undefined): string {
  if (!finalidade) return '';

  const normalizado = finalidade.trim().toUpperCase();
  return FINALIDADE_LABELS[normalizado] ?? finalidade;
}

function ControleEstoquePage() {
  const queryClient = useQueryClient();
  const metrics = useMetrics();
  const analytics = useAnalytics();
  const errors = useErrors();

  const [formData, setFormData] = useState<IControleEstoqueFormData>(INITIAL_FORM_DATA);
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [validadeError, setValidadeError] = useState('');
  const [filtroTabela, setFiltroTabela] = useState<IFiltroTabela>(INITIAL_FILTRO_TABELA);
  const [showNovaMovimentacaoModal, setShowNovaMovimentacaoModal] = useState<boolean>(false);

  const produtosQuery = useQuery({
    queryKey: controleEstoqueKeys.produtos,
    queryFn: fetchListaProdutosControleEstoque,
  });

  const estoqueFrescoQuery = useQuery({
    queryKey: controleEstoqueKeys.estoqueFresco,
    queryFn: fetchEstoqueFresco,
  });

  const inserirMutation = useMutation({
    mutationFn: (payload: IInserirMovimentacaoPayload) =>
      metrics.measure('controle_estoque.inserir', () => inserirMovimentacao(payload)),
    onSuccess: () => {
      analytics.track('Controle Estoque Movimentacao Created');
      setMessage('Movimentação enviada com sucesso!');
      setShowNovaMovimentacaoModal(false);
      void queryClient.invalidateQueries({ queryKey: controleEstoqueKeys.estoqueFresco });
    },
    onError: (error: Error) => {
      errors.capture(error, { tags: { source: 'controle-estoque.inserir' } });
      setMessage(`Erro ao enviar movimentação: ${error.message}`);
    },
  });

  const produtos = produtosQuery.data ?? [];
  const estoqueAtual = estoqueFrescoQuery.data ?? [];

  const validarDataValidade = (data: string): boolean => {
    if (!data) {
      setValidadeError('');
      return true;
    }

    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const dataValidade = new Date(data);

    if (Number.isNaN(dataValidade.getTime())) {
      setValidadeError('Data inválida');
      return false;
    }

    if (dataValidade < hoje) {
      setValidadeError('A data de validade não pode ser anterior a hoje');
      return false;
    }

    const dezAnos = new Date();
    dezAnos.setFullYear(hoje.getFullYear() + 10);
    if (dataValidade > dezAnos) {
      setValidadeError('A data de validade não pode ser superior a 10 anos');
      return false;
    }

    setValidadeError('');
    return true;
  };

  const filtrarDadosTabela = (dados: IEstoqueFrescoItem[]): IEstoqueFrescoItem[] => {
    if (!filtroTabela.buscaGeral && filtroTabela.colunaFiltro === 'todos') {
      return dados;
    }

    return dados.filter((item) => {
      const busca = filtroTabela.buscaGeral.toLowerCase();

      if (filtroTabela.colunaFiltro === 'todos') {
        return (
          item.codigo_produto?.toLowerCase().includes(busca) ||
          item.descricao_produto?.toLowerCase().includes(busca) ||
          item.lote?.toLowerCase().includes(busca) ||
          item.quantidade?.toString().toLowerCase().includes(busca) ||
          item.finalidade?.toLowerCase().includes(busca) ||
          new Date(item.validade).toLocaleDateString('pt-BR').toLowerCase().includes(busca)
        );
      }

      let valor: string | number | undefined = item[filtroTabela.colunaFiltro];
      if (filtroTabela.colunaFiltro === 'validade') {
        valor = new Date(String(valor)).toLocaleDateString('pt-BR');
      }
      return (valor?.toString().toLowerCase() ?? '').includes(busca);
    });
  };

  const ordenarDadosTabela = (dados: IEstoqueFrescoItem[]): IEstoqueFrescoItem[] => {
    return [...dados].sort((a, b) => {
      const { coluna, direcao } = filtroTabela.ordenacao;
      let valorA: string | number | Date = a[coluna] ?? '';
      let valorB: string | number | Date = b[coluna] ?? '';

      if (coluna === 'validade') {
        valorA = new Date(String(valorA));
        valorB = new Date(String(valorB));
      } else if (coluna === 'quantidade') {
        valorA = parseFloat(String(valorA)) || 0;
        valorB = parseFloat(String(valorB)) || 0;
      } else {
        valorA = valorA?.toString().toLowerCase() || '';
        valorB = valorB?.toString().toLowerCase() || '';
      }

      if (direcao === 'asc') {
        return valorA > valorB ? 1 : valorA < valorB ? -1 : 0;
      }
      return valorA < valorB ? 1 : valorA > valorB ? -1 : 0;
    });
  };

  const handleFiltroTabelaChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFiltroTabela((prev) => ({ ...prev, [name]: value }));
  };

  const handleOrdenacao = (coluna: TColunaOrdenacao) => {
    setFiltroTabela((prev) => ({
      ...prev,
      ordenacao: {
        coluna,
        direcao: prev.ordenacao.coluna === coluna && prev.ordenacao.direcao === 'asc' ? 'desc' : 'asc',
      },
    }));
  };

  const handleLimparFiltrosTabela = () => {
    setFiltroTabela(INITIAL_FILTRO_TABELA);
  };

  const handleFiltrarTabela = () => {
    // O filtro já é aplicado em tempo real ao digitar/selecionar; este botão
    // apenas reforça a intenção do usuário (ex.: foco/leitores de tela).
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === 'validade') {
      validarDataValidade(value);
    }
  };

  const handleAddMovimentacao = () => {
    if (formData.tipoMovimentacao === 'entrada') {
      if (!formData.codigoProduto || !formData.quantidade || !formData.validade || !formData.lote) {
        setMessage('Preencha todos os campos obrigatórios para entrada.');
        return;
      }

      if (!validarDataValidade(formData.validade)) {
        setMessage('Data de validade inválida. Verifique os erros abaixo.');
        return;
      }
    } else if (!formData.codigoProduto || !formData.quantidade || !formData.destino) {
      setMessage('Preencha todos os campos obrigatórios para saída.');
      return;
    }

    let validadeFormatada = formData.validade;

    if (formData.tipoMovimentacao === 'entrada' && formData.validade) {
      validadeFormatada = `${formData.validade}T00:00:00Z`;
    }

    let novaMovimentacao: IInserirMovimentacaoPayload;

    if (formData.tipoMovimentacao === 'saida') {
      const [codigoProduto, lote, validade] = formData.codigoProduto.split('|');

      const produtoSelecionado = estoqueAtual.find((item) => {
        const validadeNormalizada = normalizarData(item.validade);
        const validadeBusca = normalizarData(validade);

        return (
          item.codigo_produto === codigoProduto &&
          item.lote === lote &&
          validadeNormalizada === validadeBusca
        );
      });

      if (!produtoSelecionado) {
        setMessage('Seleção inválida. Produto não encontrado.');
        return;
      }

      if (produtoSelecionado.quantidade <= 0) {
        setMessage('Este produto não possui saldo disponível para saída.');
        return;
      }

      const quantidadeSolicitada = parseFloat(formData.quantidade);
      const saldoDisponivel = parseFloat(String(produtoSelecionado.quantidade));

      if (quantidadeSolicitada > saldoDisponivel) {
        setMessage(
          `A quantidade de saída (${quantidadeSolicitada}) excede o saldo disponível (${saldoDisponivel.toFixed(2)}).`,
        );
        return;
      }

      if (quantidadeSolicitada <= 0) {
        setMessage('A quantidade deve ser maior que zero.');
        return;
      }

      novaMovimentacao = {
        tipo_movimentacao: formData.tipoMovimentacao,
        codigo_produto: produtoSelecionado.codigo_produto,
        descricao_produto: produtoSelecionado.descricao_produto,
        lote: produtoSelecionado.lote,
        validade: produtoSelecionado.validade,
        destino: formData.destino,
        finalidade: formData.finalidade,
        quantidade: parseFloat(formData.quantidade),
      };
    } else {
      const [codigo, ...descricao] = formData.codigoProduto.split(' - ');

      novaMovimentacao = {
        tipo_movimentacao: formData.tipoMovimentacao,
        codigo_produto: codigo.trim(),
        descricao_produto: descricao.join(' ').trim(),
        lote: formData.lote,
        validade: validadeFormatada,
        destino: formData.destino || '',
        finalidade: formData.finalidade || '',
        quantidade: parseFloat(formData.quantidade),
      };
    }

    inserirMutation.mutate(novaMovimentacao);
    setFormData(INITIAL_FORM_DATA);
    setValidadeError('');
  };

  const filteredProdutos: Array<IProdutoControleEstoque | IEstoqueFrescoItem> =
    formData.tipoMovimentacao === 'entrada'
      ? produtos.filter((produto) =>
          `${produto.nome} ${produto.descricao}`
            .toLowerCase()
            .includes(searchTerm.toLowerCase()),
        )
      : estoqueAtual.filter((estoque) =>
          `${estoque.codigo_produto} ${estoque.descricao_produto}`
            .toLowerCase()
            .includes(searchTerm.toLowerCase()),
        );

  const loadError =
    (produtosQuery.isError ? 'Erro ao buscar produtos.' : '') ||
    (estoqueFrescoQuery.isError ? 'Erro ao consultar estoque.' : '');

  const hojeMin = new Date().toISOString().split('T')[0];
  const listaLancamentosPath: string = '/lista_estoque_fresco';

  return (
    <div className="controle-estoque-container">
      <div className="controle-estoque-content">
        <Card className="filter">
          <Card.Content className="filter-card__content">
            <div className="filter-grid">
              <div className="input-content filter-grid__field">
                <label htmlFor="colunaFiltro">Campo de Filtro</label>
                <select
                  id="colunaFiltro"
                  name="colunaFiltro"
                  value={filtroTabela.colunaFiltro}
                  onChange={handleFiltroTabelaChange}
                >
                  <option value="todos">Todas as colunas</option>
                  <option value="codigo_produto">Código</option>
                  <option value="descricao_produto">Descrição</option>
                  <option value="lote">Lote</option>
                  <option value="quantidade">Quantidade</option>
                  <option value="finalidade">Finalidade</option>
                  <option value="validade">Validade</option>
                </select>
              </div>

              <div className="input-content filter-grid__value">
                <label htmlFor="buscaGeral">Busca Geral:</label>
                <input
                  id="buscaGeral"
                  type="text"
                  name="buscaGeral"
                  value={filtroTabela.buscaGeral}
                  onChange={handleFiltroTabelaChange}
                  placeholder="Digite para buscar em todos os campos..."
                />
              </div>

              <button
                type="button"
                className="filter-button"
                onClick={handleFiltrarTabela}
              >
                Filtrar
              </button>

              <button
                type="button"
                className="clear-filter-button"
                onClick={handleLimparFiltrosTabela}
              >
                Limpar Filtro
              </button>
            </div>

            <div
              className="filter-card__divider"
              aria-hidden="true"
            />

            <button
              type="button"
              className="filter-button"
              style={{ width: '50%' }}
              onClick={() => setShowNovaMovimentacaoModal(true)}
            >
              Nova Movimentação
            </button>
          </Card.Content>
        </Card>

        <div className="controle-estoque-table-container">
          <Table className="estoque-table">
            <caption className="sr-only">Controle de estoque fresco</caption>
            <Table.Header>
              <Table.Row>
                {columns.map((item, index) => (
                  <Table.Head
                    key={index}
                    scope="col"
                    onClick={() => handleOrdenacao(item as TColunaOrdenacao)}
                  >
                    {item}{' '}
                    {filtroTabela.ordenacao.coluna === item &&
                      (filtroTabela.ordenacao.direcao === 'asc' ? '↑' : '↓')}
                  </Table.Head>
                ))}
              </Table.Row>
            </Table.Header>
            <Table.Content>
              {ordenarDadosTabela(filtrarDadosTabela(estoqueAtual)).map((item, index) => (
                <Table.Row
                  key={index}
                >
                  <Table.Cell>{item.codigo_produto}</Table.Cell>
                  <Table.Cell>{item.descricao_produto}</Table.Cell>
                  <Table.Cell>{item.lote}</Table.Cell>
                  <Table.Cell>{new Date(item.validade).toLocaleDateString('pt-BR') || 'N/A'}</Table.Cell>
                  <Table.Cell>
                    {item.quantidade !== undefined && item.quantidade !== null
                      ? item.quantidade.toFixed(2)
                      : '0.00'}
                  </Table.Cell>
                  <Table.Cell>{formatarFinalidade(item.finalidade)}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Content>
          </Table>
        </div>
      </div>

      {showNovaMovimentacaoModal && (
        <Modal
          className="movimentacao-modal"
          onClose={() => setShowNovaMovimentacaoModal(false)}
        >
          <Modal.Header title="Nova Movimentação" />
          <Modal.Content>
            <div className="movimentacao-modal-grid">
              <div className="input-content movimentacao-modal-grid__field">
                <label htmlFor="tipoMovimentacao">Tipo de Movimentação:</label>

                <select
                  id="tipoMovimentacao"
                  name="tipoMovimentacao"
                  value={formData.tipoMovimentacao}
                  onChange={handleChange}
                >
                  <option value="entrada">Entrada</option>
                  <option value="saida">Saída</option>
                </select>
              </div>

              <div className="input-content movimentacao-modal-grid__field">
                <label htmlFor="searchTerm">Pesquisar Produto:</label>

                <input
                  id="searchTerm"
                  type="text"
                  placeholder="Digite para filtrar..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <div className="input-content movimentacao-modal-grid__field">
                <label htmlFor="codigoProduto">Código do Produto:</label>

                <select
                  id="codigoProduto"
                  name="codigoProduto"
                  value={formData.codigoProduto}
                  onChange={handleChange}
                >
                  <option value="">Selecione um produto</option>
                  {filteredProdutos.map((item, index) => {
                    const isEntrada = formData.tipoMovimentacao === 'entrada';
                    const produto = item as IProdutoControleEstoque;
                    const estoque = item as IEstoqueFrescoItem;

                    const textoCompleto = isEntrada
                      ? `${produto.nome} - ${produto.descricao}`
                      : `${estoque.codigo_produto} - ${estoque.descricao_produto} | Lote: ${estoque.lote} | Validade: ${new Date(estoque.validade).toLocaleDateString('pt-BR')} | Saldo: ${estoque.quantidade}`;

                    const optionValue = isEntrada
                      ? `${produto.nome} - ${produto.descricao}`
                      : `${estoque.codigo_produto}|${estoque.lote}|${estoque.validade}`;

                    return (
                      <option
                        key={isEntrada ? produto.id : index}
                        value={optionValue}
                        title={textoCompleto}
                      >
                        {textoCompleto}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div className="input-content movimentacao-modal-grid__field">
                <label htmlFor="quantidade">Quantidade:</label>

                <input
                  id="quantidade"
                  type="number"
                  name="quantidade"
                  value={formData.quantidade}
                  onChange={handleChange}
                />
              </div>

              {formData.tipoMovimentacao === 'entrada' && (
                <>
                  <div className="input-content movimentacao-modal-grid__field">
                    <label htmlFor="validade">Validade:</label>

                    <input
                      id="validade"
                      type="date"
                      name="validade"
                      value={formData.validade}
                      onChange={handleChange}
                      className={validadeError ? 'error' : ''}
                      min={hojeMin}
                    />

                    {validadeError && <div className="error-message">{validadeError}</div>}
                  </div>

                  <div className="input-content movimentacao-modal-grid__field">
                    <label htmlFor="lote">Lote:</label>

                    <input
                      id="lote"
                      type="text"
                      name="lote"
                      value={formData.lote}
                      onChange={handleChange}
                    />
                  </div>
                </>
              )}

              {formData.tipoMovimentacao === 'saida' ? (
                <div className="input-content movimentacao-modal-grid__field">
                  <label htmlFor="destino">Destino:</label>

                  <select
                    id="destino"
                    name="destino"
                    value={formData.destino}
                    onChange={handleChange}
                  >
                    <option value="">Selecione o destino</option>
                    <option value="VENDA">Venda</option>
                    <option value="PRODUÇÃO">Produção</option>
                    <option value="INVENTARIO">Inventário</option>
                  </select>
                </div>
              ) : (
                <div className="input-content movimentacao-modal-grid__field">
                  <label htmlFor="finalidade">Finalidade:</label>

                  <select
                    id="finalidade"
                    name="finalidade"
                    value={formData.finalidade}
                    onChange={handleChange}
                  >
                    <option value="">Selecione a finalidade</option>
                    <option value="VENDA">Venda</option>
                    <option value="INDUSTRIALIZAÇÃO">Industrialização</option>
                    <option value="PRODUÇÃO">Produção</option>
                    <option value="EXPORTAÇÃO">Exportação</option>
                    <option value="REVENDA">Revenda</option>
                  </select>
                </div>
              )}
            </div>
          </Modal.Content>

          <Modal.Footer>
            <button
              type="button"
              className="filter-button"
              onClick={handleAddMovimentacao}
              disabled={inserirMutation.isPending}
            >
              Confirmar Movimentação
            </button>

            <button
              type="button"
              className="clear-filter-button"
              onClick={() => setShowNovaMovimentacaoModal(false)}
            >
              Cancelar
            </button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}
